import { assign } from 'fsm';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Raven from 'raven-js';
import { fetchSmartProperties, runSmartProperties } from './client';
import { getObjectType, isContact, wait } from '../../utils';
import { ErrorAlertMessage } from '../../ErrorAlertMessage';
import { usageTracker } from '../../../usageTracker';
import { getEnrichmentTypeFromObjectTypeId } from '../../../utils/getEnrichmentTypeFromEnrichmentId';
import { jsx as _jsx } from "react/jsx-runtime";
export const machine = {
  initial: 'initializing',
  context: {
    enrichmentType: 'company',
    errorId: null,
    objectId: '',
    objectTypeId: null,
    onClose: () => {},
    properties: [],
    propertyDefinitions: {},
    propertySources: {},
    propertyUpdates: {},
    selectedProperties: []
  },
  states: {
    initializing: {
      always: [{
        target: 'loadingUpdates',
        actions: ['setEnrichmentType', 'trackOpenPanel']
      }]
    },
    loadingUpdates: {
      invoke: {
        src: 'fetchSmartProperties',
        onDone: [{
          target: 'idle',
          actions: ['setSmartProperties', 'setInitialPropertiesSelection']
        }, {
          target: 'idle',
          actions: ['setEmptyProperties']
        }],
        onError: [{
          cond: 'hasValidationError',
          target: 'errorValidation',
          actions: ['setErrorId', 'logError']
        }, {
          cond: 'hasNoEnrichmentData',
          target: 'errorUpdates',
          actions: ['setErrorId', 'logError']
        }, {
          target: 'error',
          actions: ['setErrorId', 'logError']
        }]
      },
      tags: ['loading']
    },
    idle: {
      on: {
        SELECT_PROPERTIES: {
          target: 'idle',
          actions: ['setSelectedProperties']
        },
        ENRICH: 'enriching',
        CLOSE: {
          target: 'idle',
          actions: ['closeDrawer']
        }
      }
    },
    enriching: {
      invoke: {
        src: 'runSmartProperties',
        onDone: {
          target: 'enrichedProperties',
          actions: ['addEnrichSuccess']
        },
        onError: {
          target: 'errorEnrichment',
          actions: ['setErrorId', 'addEnrichError', 'logError']
        }
      }
    },
    enrichedProperties: {
      invoke: {
        src: 'wait',
        onDone: {
          target: 'idle',
          actions: ['trackEnrichedRecord', 'closeDrawerAfterEnriched']
        }
      }
    },
    error: {
      on: {
        CLOSE: {
          target: 'idle',
          actions: ['closeDrawer']
        },
        RETRY: 'loadingUpdates'
      },
      tags: ['error']
    },
    errorEnrichment: {
      on: {
        SELECT_PROPERTIES: {
          target: 'idle',
          actions: ['setSelectedProperties']
        },
        ENRICH: 'enriching',
        CLOSE: {
          target: 'idle',
          actions: ['closeDrawer']
        }
      }
    },
    errorUpdates: {
      on: {
        CLOSE: {
          target: 'idle',
          actions: ['closeDrawer']
        }
      },
      tags: ['error']
    },
    errorValidation: {
      on: {
        CLOSE: {
          target: 'idle',
          actions: ['closeDrawer']
        }
      },
      tags: ['error']
    }
  },
  actions: {
    addEnrichError: context => {
      const {
        addAlertDanger,
        companyLabelLowercase,
        contactLabelLowercase,
        errorId,
        objectTypeId
      } = context;
      const objectType = getObjectType(objectTypeId);
      const Message = () => /*#__PURE__*/_jsx(ErrorAlertMessage, {
        errorId: errorId,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "enrichmentProperties.panel.alerts.error.description"
        })
      });
      addAlertDanger === null || addAlertDanger === void 0 || addAlertDanger({
        message: /*#__PURE__*/_jsx(Message, {}),
        titleText: I18n.text('enrichmentProperties.panel.alerts.error.title', {
          objectType: isContact(objectType) ? contactLabelLowercase : companyLabelLowercase
        })
      });
    },
    addEnrichSuccess: context => {
      const {
        addAlertSuccess,
        companyLabel,
        contactLabel,
        objectTypeId,
        selectedProperties
      } = context;
      const objectType = getObjectType(objectTypeId);
      const Message = () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: "enrichmentProperties.panel.alerts.success.description",
        options: {
          count: selectedProperties.length
        }
      });
      addAlertSuccess === null || addAlertSuccess === void 0 || addAlertSuccess({
        message: /*#__PURE__*/_jsx(Message, {}),
        titleText: I18n.text('enrichmentProperties.panel.alerts.success.title', {
          objectType: isContact(objectType) ? contactLabel : companyLabel
        })
      });
    },
    closeDrawer: context => {
      var _context$onClose;
      (_context$onClose = context.onClose) === null || _context$onClose === void 0 || _context$onClose.call(context, false);
    },
    closeDrawerAfterEnriched: context => {
      var _context$onClose2;
      (_context$onClose2 = context.onClose) === null || _context$onClose2 === void 0 || _context$onClose2.call(context, true);
    },
    logError: (_context, event) => {
      const {
        responseJSON
      } = event.data;
      Raven.captureException(responseJSON);
    },
    setEnrichmentType: assign(context => {
      return {
        enrichmentType: getEnrichmentTypeFromObjectTypeId(context.objectTypeId)
      };
    }),
    setErrorId: assign((_context, event) => {
      const {
        responseJSON
      } = event.data;
      const {
        correlationId
      } = responseJSON;
      return {
        errorId: correlationId
      };
    }),
    setEmptyProperties: assign(() => {
      return {
        properties: []
      };
    }),
    setInitialPropertiesSelection: assign(context => {
      return {
        selectedProperties: context.properties.map(property => {
          return property.propertyName;
        })
      };
    }),
    setSmartProperties: assign((_context, event) => {
      return {
        properties: event.data
      };
    }),
    setSelectedProperties: assign((_context, event) => {
      return {
        selectedProperties: event.data.propertyNames
      };
    }),
    trackEnrichedRecord: context => {
      usageTracker.track('enriched-record', {
        objectType: getObjectType(context.objectTypeId),
        enrichmentType: 'smartPropertySingle',
        selectionType: 'manual',
        propertyCount: 0,
        selectionCount: 0,
        selectionDifference: 0
      });
    },
    trackOpenPanel: context => {
      usageTracker.track('interaction', {
        screen: 'smart-properties-panel',
        subscreen: getObjectType(context.objectTypeId),
        action: 'opened-smart-properties-panel'
      });
    }
  },
  guards: {
    hasNoEnrichmentData: (_context, event) => {
      const {
        status,
        responseJSON
      } = event.data;
      const {
        category
      } = responseJSON;
      return status === 404 && category === 'OBJECT_NOT_FOUND';
    },
    hasValidationError: (_context, event) => {
      const {
        status,
        responseJSON
      } = event.data;
      const {
        category
      } = responseJSON;
      return status === 400 && category === 'VALIDATION_ERROR';
    },
    hasPropertyUpdates: (_context, event) => {
      return Object.keys(event.data).length > 0;
    }
  },
  services: {
    fetchSmartProperties,
    runSmartProperties,
    wait: () => wait()
  }
};